import React, { useEffect, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { 
  Container, Row, Col, Form, InputGroup, Button, 
  Table, Badge, Modal, Spinner, Card 
} from "react-bootstrap";
import { motion, AnimatePresence } from "framer-motion";
import { Search, ArrowLeft, User, AlertCircle, Clock, Layers } from "react-feather";

import { getAppointments, getDoctors, searchCustomers } from "../../../../Data/Customer/api";
import { getCurrentISTTime } from "./currentTime";
import { getUserRights } from "../../CRM-Calicut/utils/userRights";
import { compareTimes, generateTimeOptions, isAppointmentAvailable } from "./utils/appointmentValidation";

// Framer Motion Animation Presets
const pageTransition = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: "easeOut" } }
};

const tableRowVariants = {
  hidden: { opacity: 0, x: -5 },
  visible: i => ({
    opacity: 1, 
    x: 0,
    transition: { delay: i * 0.02, ease: "linear" }
  })
};

const Booking = () => {
  let savedToggle = false;
  const [userRights, setUserRights] = useState("");
  const navigate = useNavigate();
  const auth = useSelector((state) => state.authentication);
  const { userData } = auth;
  const [modal, setModal] = useState(false);
  const toggle = () => setModal(!modal);
  
  const location = useLocation();
  const data = location.state;
  const [selectedDate, setSelectedDate] = useState(new Date());

  const nDate = new Date(selectedDate);
  const datewithoutHours = new Date(new Date(nDate.setHours(0, 0, 0, 0)));
  const [selectedDoctor, setSelectedDoctor] = useState("");
  const [selectedTime, setSelectedTime] = useState("");
  const [selectedEndTime, setSelectedEndTime] = useState("");
  const [appointmentData, setAppointmentData] = useState([]);
  const [appointmentDataFilter, setAppointmentDataFilter] = useState("");
  
  const token = JSON.parse(sessionStorage.getItem("token"));
  const username = JSON.parse(sessionStorage.getItem("username"));
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setisSaving] = useState(false);
  
  const [newPatientFName, setNewPatientFName] = useState("");
  const [newPatientSName, setNewPatientSName] = useState("");
  const [newPatientMobile, setNewPatientMobile] = useState("");
  const [filteredTimeOptions, setFilteredTimeOptions] = useState([]);
  const [doctorsData, setDoctorsData] = useState(
    JSON.parse(localStorage.getItem("doctorsData")) || []
  );
  const [selectedDepartment, setSelectedDepartment] = useState("");
  const [customerSearchInput, setCustomerSearchInput] = useState("");
  const [mrn, setMRN] = useState(`${Date.now()}`);
  const visitTypeOptions = ["First Visit", "Follow Up", "Re Visit"];
  const [optionsDepartment, setOptionsDepartment] = useState([]);
  const [edit, setEdit] = useState(false);
  const [_id, setId] = useState("");
  const [selectedNextTime, setSelectedNextTime] = useState("");
  const [bookedSlots, setBookedSlots] = useState("");
  const [selectedVisitType, setSelectedVisitType] = useState(visitTypeOptions[0]);
  const [reasonforVisit, setReasonforVisit] = useState("");
  const [selectedAppointment, setSelectedAppointment] = useState("");
  const [additional, setAdditional] = useState({ advancePayment: "", status: "" });

  const reasonforvisitOptions = [
    "Dermatology Consultation", "Cosmetology Consultation", "Hair Problems",
    "Weight Loss / Liposuction", "Hair Transplantation", "Skin Treatment",
    "Dental Consultation", "Dental Treatment", "General Checkup",
    "Homeopathy Consultation", "Physiotherapy Consultation", "Ayurveda Consultation",
    "Nutrition Consultation", "Fever / Cold / Cough", "Diabetes / Hypertension", "Other"
  ];

  const timeOptions = generateTimeOptions(selectedDoctor) || [];
  
  const fdate = new Date(selectedDate);
  const ISTOffset = 330; 
  const ISTTime = new Date(fdate.getTime() + ISTOffset * 60000); 
  const formattedDate = !isNaN(ISTTime.getTime()) ? ISTTime.toISOString().split("T")[0] : "";
  const branch = sessionStorage.getItem("branch") ? JSON.parse(sessionStorage.getItem("branch")) : "";

  // Original Logic: Fetch Appointments
  const getAppointmentsHandler = () => {
    const datTemp = new Date(selectedDate); 
    const datetofetch = new Date(datTemp.setHours(0, 0, 0, 0)); 
    setIsLoading(true);
    getAppointments(token, datetofetch, branch, "", selectedDoctor, selectedDepartment, navigate)
      .then((res) => {
        setIsLoading(false);
        setAppointmentData(res || []);
      })
      .catch((err) => {
        console.error(err);
        setIsLoading(false);
      });
  };

  // Original Logic: Clear Form Data
  const clearForm = () => {
    setMRN(() => `Tmp-${Date.now()}`);
    setNewPatientFName("");
    setNewPatientSName("");
    setNewPatientMobile("");
  };

  // Original Logic: Future Check
  const isTodayGrt = (date) => {
    const today = new Date();
    const selDate = new Date(date);
    if (selDate.getDate() < today.getDate() && selDate.getMonth() === today.getMonth()) return true;
    return selDate.getMonth() < today.getMonth() && selDate.getFullYear() < today.getFullYear();
  };

  // Original Logic: Availability Validator
  const isBookingAllowed = () => {
    return !appointmentData.some((apmnt) => {
      let x = 0, y = 0, over = 0;
      if (selectedEndTime) {
        x = compareTimes(selectedEndTime, apmnt.endTime);
        over = compareTimes(selectedEndTime, apmnt.time);
      }
      if (selectedTime) y = compareTimes(selectedTime, apmnt.time);
      return (
        (apmnt.time === selectedTime && apmnt.mrn) ||
        (apmnt.endTime === selectedEndTime && apmnt.mrn) ||
        (x === -1 && y === 1 && apmnt.mrn) ||
        (over > 0 && y < 0)
      );
    });
  };

  // Original Logic: History Interceptor
  const verifyDateHandler = () => {
    if (isTodayGrt(selectedDate)) {
      return !window.confirm("Selected date is not in the future. Do you want to continue booking for old date?");
    }
    return false;
  };

  // Original Logic: Create Booking
  const bookingHandler = () => {
    setisSaving(true);
    const postData = {
      mrn: mrn || (data?.otData && data.otData.mrn),
      date: datewithoutHours,
      time: selectedTime,
      endTime: selectedEndTime,
      doctor: selectedDoctor,
      createdBy: username,
      fname: newPatientFName,
      sname: newPatientSName,
      contactno: newPatientMobile,
      department: selectedDepartment,
      visitType: selectedVisitType,
      visitReason: reasonforVisit,
      additional,
    };

    if (savedToggle) {
      alert("Already Saved");
      setisSaving(false);
      return;
    }

    let x = 0, y = 0;
    if (selectedNextTime && bookedSlots) x = compareTimes(selectedNextTime, bookedSlots.endTime);
    if (selectedTime && bookedSlots) y = compareTimes(selectedTime, bookedSlots.time);
    
    if (bookedSlots && (
      (bookedSlots.time === selectedTime && bookedSlots.mrn) ||
      (bookedSlots.endTime === selectedNextTime && bookedSlots.mrn) ||
      (x === -1 && y === 1 && bookedSlots.mrn)
    )) {
      alert("Booking Not Allowed");
      toggle();
      setisSaving(false);
      return;
    }

    if (!isBookingAllowed()) {
      alert("Booking Not Allowed, time slot conflict");
      toggle();
      setisSaving(false);
      return;
    }

    if (!newPatientMobile || newPatientMobile.length < 10) { alert("Please enter Contact No"); setisSaving(false); return; }
    if (!newPatientFName || newPatientFName.length < 2) { alert("Please enter First Name"); setisSaving(false); return; }
    if (!selectedDoctor) { alert("Select Doctor"); setisSaving(false); return; }
    if (!selectedDepartment) { alert("Select Department"); setisSaving(false); return; }
    if (!selectedDate) { alert("Select Date"); setisSaving(false); return; }
    if (!selectedVisitType) { alert("Select Visit Type"); setisSaving(false); return; }
    if (!reasonforVisit) { alert("Select Visit Reason"); setisSaving(false); return; }
    if (selectedTime === selectedEndTime || !selectedEndTime) { alert("Please select Proper Slot End Time"); setisSaving(false); return; }

    if (verifyDateHandler()) {
      setisSaving(false);
      return;
    }

    axios.post(`${process.env.REACT_APP_CRI_API}add-appointment`, postData, {
      params: { branch },
      headers: { Authorization: `Bearer ${token}` },
    })
    .then((res) => {
      savedToggle = true;
      setBookedSlots(res.data);
      getAppointmentsHandler();
      alert("Appointment Successful");
      toggle();
      setisSaving(false);
    })
    .catch((err) => {
      setisSaving(false);
      alert(err.message);
      handleTokenExpiry(err);
    });
  };

  // Original Logic: Delete Appointment
  const deleteAppointmentHandler = () => {
    axios
      .delete(`${process.env.REACT_APP_CRI_API}delete-appointment`, {
        params: { date: datewithoutHours, id: _id, branch: branch },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        if (res) {
          getAppointmentsHandler();
          toggle();
        }
      })
      .catch((err) => {
        handleTokenExpiry(err);
      });
  };

  // Original Logic: Update Appointment
  const updateAppointmentHandler = () => {
    const postData = {
      mrn: mrn || (data?.otData && data.otData.mrn),
      date: datewithoutHours,
      time: selectedTime,
      endTime: selectedEndTime,
      doctor: selectedDoctor,
      createdBy: username,
      fname: newPatientFName,
      sname: newPatientSName,
      contactno: newPatientMobile,
      department: selectedDepartment,
      visitType: selectedVisitType,
      visitReason: reasonforVisit,
      additional,
    };
    axios
      .post(`${process.env.REACT_APP_CRI_API}edit-appointment`, postData, {
        params: { id: _id, branch: branch },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        if (res) {
          getAppointmentsHandler();
          toggle();
        }
      })
      .catch((err) => {
        console.error(err);
        handleTokenExpiry(err);
      });
  };

  const handleTokenExpiry = (err) => {
    if (err?.response?.data && (err.response.data.err?.name === "TokenExpiredError" || err.response.data.message === "Unauthorized")) {
      sessionStorage.clear();
      navigate("/cri-crm");
    }
  };

  const filterTimeOptions = (startTime) => {
    return timeOptions.filter((time, index) => {
      const startT = compareTimes(startTime, time);
      const nextTime = timeOptions[index + 1];
      return startT === -1 && nextTime !== undefined;
    });
  };

// This hook ONLY cares about setting the department when we have a doctor and the doctor data
useEffect(() => {
  if (selectedDoctor && doctorsData && doctorsData.length > 0) {
    const matchedDoc = doctorsData.find(d => d.doctorName === selectedDoctor);
    if (matchedDoc && matchedDoc.department) {
      setSelectedDepartment(matchedDoc.department);
      
      // Update options so the dropdown actually has the value to display
      setOptionsDepartment([matchedDoc]);
    }
  }
}, [selectedDoctor, doctorsData]);

  useEffect(() => {
    if (selectedDoctor && selectedDate) {
      getAppointmentsHandler();
    }
  }, [selectedDoctor, selectedDate]);

  useEffect(() => {
    if (selectedAppointment) {
      setNewPatientFName(selectedAppointment.fname || "");
      setNewPatientSName(selectedAppointment.sname || "");
      setMRN(selectedAppointment.mrn || "");
      setNewPatientMobile(selectedAppointment.contactno || "");
      setSelectedVisitType(selectedAppointment.visitType || visitTypeOptions[0]);
      setReasonforVisit(selectedAppointment.visitReason || "");
    }
  }, [selectedAppointment]);

  const searchHandler = () => {
    if (selectedDoctor) {
      getAppointmentsHandler();
    } else {
      alert("Please select doctor");
    }

    if (customerSearchInput) {
      searchCustomers(token, customerSearchInput, branch, navigate).then((res) => {
        if (res && res.length > 0) {
          setNewPatientFName(res[0].fname);
          setNewPatientSName(res[0].sname);
          setMRN(res[0].mrn);
          setNewPatientMobile(res[0].contactno);
        } else {
          alert("Patient not registered");
        }
      });
    }
  };

  // 1. Fetch Doctors only when needed
useEffect(() => {
  if (!doctorsData.length && branch && token) {
    getDoctors(token, branch, navigate).then((res) => {
      setDoctorsData(res || []);
      localStorage.setItem("doctorsData", JSON.stringify(res));
    });
  }
}, [branch, token]); // Only depend on branch/token

// 2. Process incoming navigation data ONLY once
// Process incoming navigation data
useEffect(() => {
  if (data) {
    const source = data.customerData || data.otData;
    if (source && source.doctorName) {
      setNewPatientFName(source.fname || "");
      setNewPatientSName(source.sname || "");
      setNewPatientMobile(source.contactno || source.mobile || "");
      setMRN(source.mrn || `${Date.now()}`);
      
      // SET BOTH STATES AT THE SAME TIME
      setSelectedDoctor(source.doctorName);
      
      // Look inside current doctorsData state to find the department
      const matchedDoc = doctorsData.find(
        (d) => d.doctorName.trim().toLowerCase() === source.doctorName.trim().toLowerCase()
      );
      
      if (matchedDoc) {
        setSelectedDepartment(matchedDoc.department);
      }
    }
  }
}, [data, doctorsData]); // This will trigger once as soon as both are available

// 3. Set User Rights separately
useEffect(() => {
  getUserRights().then((res) => {
    setUserRights(res);
    if (res === "Doctor" && userData?.user) {
      setSelectedDoctor(userData.user);
    }
  });
}, [userData]); // Only depend on userData

  useEffect(() => {
    if (selectedTime) {
      setFilteredTimeOptions(filterTimeOptions(selectedTime));
    }
  }, [selectedTime]);

  return (
    <motion.div 
      initial="hidden"
      animate="visible"
      variants={pageTransition}
      className="bg-light min-vh-100 py-3"
    >
      <Container fluid="lg">
        {/* Top Control Header */}
        <Card className="border-0 shadow-sm bg-white mb-4 rounded-3 overflow-hidden">
          <Card.Body className="p-3 d-flex flex-wrap align-items-center justify-content-between gap-2">
            <div className="d-flex align-items-center gap-2">
              <Button 
                variant="outline-secondary" 
                size="sm" 
                className="d-flex align-items-center justify-content-center border-0 bg-light rounded-pill p-2"
                onClick={() => navigate(userRights === "Doctor" ? "/trtmnt" : (branch === "CLT" ? "/crm-clt" : "/crm"))}
              >
                <ArrowLeft size={16} />
              </Button>
              <h5 className="mb-0 fw-bold text-dark">Appointment Console</h5>
            </div>
            
            <div className="d-flex align-items-center gap-3 bg-light px-3 py-2 rounded-pill border">
              <span className="small text-secondary d-flex align-items-center gap-1">
                <User size={14} className="text-primary" /> 
                <strong>Operator:</strong> {userData?.user || username}
              </span>
              {appointmentData.length > 0 && (
                <Badge bg="primary" className="px-2.5 py-1.5 rounded-pill fw-medium">
                  {appointmentData.length} Booked
                </Badge>
              )}
            </div>
          </Card.Body>
        </Card>

        {/* Global Controls Row */}
        <Row className="g-3 mb-4">
          <Col md={12} lg={4}>
            <Form.Group className="mb-0">
              <Form.Control 
                type="date" 
                className="shadow-sm border-0 bg-white p-3 ps-4 rounded-3 text-secondary"
                value={formattedDate}
                onChange={(e) => {
                  if (e.target.value) {
                    setSelectedDate(e.target.value);
                    setAppointmentData([]);
                  }
                }}
              />
            </Form.Group>
          </Col>

          <Col md={6} lg={4}>
            {userData?.rights === "Doctor" ? (
              <Form.Control type="text" value={userData.user} disabled className="shadow-sm border-0 p-3 rounded-3 bg-white" />
            ) : (
              <Form.Select
                className="shadow-sm border-0 bg-white p-3 rounded-3 text-secondary"
                value={selectedDoctor}
                onChange={(e) => {
                  setSelectedDoctor(e.target.value);
                  setAppointmentData([]);
                }}
              >
                <option value="" disabled>Choose Doctor...</option>
                {doctorsData?.map((doc, idx) => (
                  <option key={idx} value={doc.doctorName}>{doc.doctorName}</option>
                ))}
              </Form.Select>
            )}
          </Col>

          <Col md={6} lg={4}>
            <InputGroup className="shadow-sm rounded-3 overflow-hidden bg-white">
       <Form.Select
  className="border-0 bg-transparent p-3 text-secondary"
  value={selectedDepartment || ""}
  onChange={(e) => setSelectedDepartment(e.target.value)}
>
  <option value="">All Departments...</option>
  {/* Map directly from doctorsData to ensure we always have the list */}
  {[...new Set(doctorsData.map(d => d.department))].map((dept, idx) => (
    <option key={idx} value={dept}>
      {dept}
    </option>
  ))}
</Form.Select>
              <Button 
                variant="primary" 
                className="px-4 d-flex align-items-center justify-content-center"
                onClick={searchHandler}
              >
                <Search size={18} />
              </Button>
            </InputGroup>
          </Col>
        </Row>

        {/* Main Interface Content */}
        <AnimatePresence mode="wait">
          {isLoading ? (
            <motion.div 
              initial={{ opacity: 0 }} 
              animate={{ opacity: 1 }} 
              exit={{ opacity: 0 }}
              className="d-flex justify-content-center align-items-center p-5 bg-white shadow-sm rounded-3"
            >
              <Spinner animation="border" variant="primary" />
            </motion.div>
          ) : !selectedDoctor ? (
            <motion.div className="text-center text-muted p-5 bg-white shadow-sm rounded-3 border-0">
              <AlertCircle size={40} className="text-secondary mb-2 opacity-50" />
              <p className="mb-0 fw-medium">Please select a doctor to load the real-time schedule grid.</p>
            </motion.div>
          ) : (
            <motion.div 
              initial={{ opacity: 0 }} 
              animate={{ opacity: 1 }}
              className="bg-white rounded-3 shadow-sm overflow-hidden"
            >
              <div className="table-responsive">
                <Table hover align="middle" className="mb-0 border-0 align-middle">
                  <thead className="bg-light border-bottom text-secondary uppercase small tracking-wider">
                    <tr>
                      <th className="py-3 ps-4 border-0">Time Coordinates</th>
                      <th className="py-3 border-0">Availability Status</th>
                      <th className="py-3 border-0">Indication / Reason</th>
                      <th className="py-3 border-0">Visit Segment</th>
                      <th className="py-3 border-0">Department</th>
                      <th className="py-3 border-0 pe-4">Metrics/Contact</th>
                    </tr>
                  </thead>
                 <tbody>
                  {timeOptions.map((time, index) => {
                    const nextTime = timeOptions[index + 1];
                    if (!nextTime) return null;
                    const timeRange = `${time} - ${nextTime}`;
                    
                    let xp = 0, yp = 0, overp = 0;
                    const matchedAppointment = appointmentData?.find((apmnt) => {
                      if (nextTime) { 
                        xp = compareTimes(nextTime, apmnt.endTime); 
                        overp = compareTimes(nextTime, apmnt.time); 
                      }
                      if (time) yp = compareTimes(time, apmnt.time);
                      return (((apmnt.time === time) || (apmnt.endTime === nextTime) || (xp === -1 && yp === 1) || (overp > 0 && yp < 0)) && apmnt.mrn);
                    });

                    const hasPatient = !!matchedAppointment;

                    return (
                      <motion.tr 
                        key={index}
                        custom={index}
                        variants={tableRowVariants}
                        initial="hidden"
                        animate="visible"
                        className="border-bottom border-light"
                      >
                        <td className="fw-semibold text-dark py-3 ps-4">
                          <span className="d-flex align-items-center gap-2">
                            <Clock size={14} className="text-secondary" /> {timeRange}
                          </span>
                        </td>
                        <td>
                          {hasPatient ? (
                            <Badge 
                              bg="danger" 
                              className="bg-opacity-10 text-danger px-3 py-2 rounded-pill fw-medium"
                              style={{ cursor: 'pointer' }}
                              onClick={() => {
                                setId(matchedAppointment._id);
                                setSelectedAppointment(matchedAppointment);
                                setAdditional({
                                  advancePayment: matchedAppointment.additional?.advancePayment || "",
                                  status: matchedAppointment.additional?.status || ""
                                });
                                setEdit(true);
                                setSelectedTime(time);
                                setSelectedEndTime(nextTime);
                                toggle();
                              }}
                            >
                              Occupied Slot (Edit ✎)
                            </Badge>
                          ) : (
                            <Badge 
                              bg="success" 
                              className="bg-opacity-10 text-success px-3 py-2 rounded-pill fw-medium"
                              style={{ cursor: 'pointer' }}
                            // Inside your table .map loop for the "Create Booking +" badge:
                          onClick={() => {
                            setEdit(false);
                            // Remove clearForm() here if you want the navigation data to persist 
                            // until the user actually decides to create a *new* different booking.
                            // OR, specifically clear only if you don't have existing navigation state.
                            if (!data) {
                              clearForm();
                            }
                            setSelectedAppointment("");
                            setAdditional({ advancePayment: "", status: "" });
                            setSelectedTime(time);
                            setSelectedEndTime(nextTime);
                            toggle();
                          }}
                            >
                              Create Booking +
                            </Badge>
                          )
                        }
                        </td>
                        <td className="text-dark fw-medium">
                          {hasPatient ? matchedAppointment.visitReason : "—"}
                        </td>
                        <td>
                          {hasPatient ? (
                            <Badge bg="secondary" className="bg-opacity-10 text-secondary border fw-normal px-2.5 py-1">
                              {matchedAppointment.visitType}
                            </Badge>
                          ) : "—"}
                        </td>
                        <td className="text-secondary">
                          {hasPatient ? matchedAppointment.department : "—"}
                        </td>
                        <td className="pe-4">
                          {hasPatient ? (
                            <div className="d-flex flex-column">
                              <span className="fw-semibold text-dark">{matchedAppointment.fname} {matchedAppointment.sname}</span>
                              <span className="text-muted small">{matchedAppointment.contactno}</span>
                            </div>
                          ) : "—"}
                        </td>
                      </motion.tr>
                    );
                  })}
                </tbody>
                </Table>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </Container>

      {/* Booking Form Overlay Modal Container */}
      <Modal show={modal} onHide={toggle} centered size="lg" className="border-0">
        <Modal.Header closeButton className="border-0 pb-0 pt-4 px-4 bg-white">
          <Modal.Title className="fw-bold text-dark d-flex align-items-center gap-2">
            <Layers className="text-primary" size={20} />
            {edit ? "Configure Slot Parameters" : "Initiate Time Reservation"}
          </Modal.Title>
        </Modal.Header>
        
       <Modal.Body className="p-4 bg-white">
  <div className="p-3 bg-light rounded-3 d-flex flex-wrap justify-content-between align-items-center border mb-4 gap-2">
    {/* ADDED DATE SECTION HERE */}
    <div>
      <span className="text-muted small d-block">Appointment Date</span>
      <span className="fw-semibold text-dark">{formattedDate || "—"}</span>
    </div>
    
    <div>
      <span className="text-muted small d-block">Selected Doctor</span>
      <span className="fw-semibold text-dark">{selectedDoctor || "Not selected"}</span>
    </div>
    <div>
      <span className="text-muted small d-block">Department</span>
      <span className="fw-semibold text-dark">{selectedDepartment || "Not selected"}</span>
    </div>
    <div>
      <span className="text-muted small d-block">Schedule Window</span>
      <span className="fw-semibold text-primary d-flex align-items-center gap-1">
        <Clock size={14} /> {selectedTime || "—"} - {selectedEndTime || "—"}
      </span>
    </div>
  </div>

          <Form>
            <h6 className="text-primary fw-bold mb-3 small uppercase tracking-wider">1. Search Existing Registration / System MRN</h6>
            <Row className="g-3 mb-4">
              <Col xs={12}>
                <InputGroup className="shadow-sm rounded-3 overflow-hidden border">
                  <Form.Control 
                    type="text" 
                    placeholder="Query by Patient Name, MRN, or Phone Number..." 
                    value={customerSearchInput}
                    onChange={(e) => setCustomerSearchInput(e.target.value)}
                    className="border-0 p-2.5"
                  />
                  <Button variant="dark" className="px-4 d-flex align-items-center gap-2" onClick={searchHandler}>
                    <Search size={16} /> Link Profile
                  </Button>
                </InputGroup>
              </Col>
            </Row>

            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="text-primary fw-bold mb-0 small uppercase tracking-wider">2. Patient Demographics & Profile</h6>
              <Button variant="link" size="sm" className="text-decoration-none text-danger p-0 fw-medium" onClick={clearForm}>
                Reset Profile Card
              </Button>
            </div>

            <Row className="g-3 mb-4">
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="text-secondary small fw-medium mb-1">Assigned MRN Identification</Form.Label>
                  <Form.Control type="text" value={mrn} disabled className="bg-light border-light fw-semibold text-secondary p-2.5 rounded-3" />
                </Form.Group>
              </Col>
              
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="text-secondary small fw-medium mb-1">First Name <span className="text-danger">*</span></Form.Label>
                  <Form.Control 
                    type="text" 
                    placeholder="Enter first name"
                    value={newPatientFName}
                    onChange={(e) => setNewPatientFName(e.target.value)}
                    className="p-2.5 rounded-3 border-light shadow-sm"
                  />
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group>
                  <Form.Label className="text-secondary small fw-medium mb-1">Surname / Last Name</Form.Label>
                  <Form.Control 
                    type="text" 
                    placeholder="Enter last name"
                    value={newPatientSName}
                    onChange={(e) => setNewPatientSName(e.target.value)}
                    className="p-2.5 rounded-3 border-light shadow-sm"
                  />
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group>
                  <Form.Label className="text-secondary small fw-medium mb-1">Mobile / Contact Number <span className="text-danger">*</span></Form.Label>
                  <Form.Control 
                    type="tel" 
                    maxLength={10}
                    placeholder="10-digit number"
                    value={newPatientMobile}
                    onChange={(e) => setNewPatientMobile(e.target.value)}
                    className="p-2.5 rounded-3 border-light shadow-sm"
                  />
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group>
                  <Form.Label className="text-secondary small fw-medium mb-1">Visit Classification <span className="text-danger">*</span></Form.Label>
                  <Form.Select
                    value={selectedVisitType}
                    onChange={(e) => setSelectedVisitType(e.target.value)}
                    className="p-2.5 rounded-3 border-light shadow-sm text-secondary"
                  >
                    {visitTypeOptions.map((type, index) => (
                      <option key={index} value={type}>{type}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group>
                  <Form.Label className="text-secondary small fw-medium mb-1">Indication / Reason for Visit <span className="text-danger">*</span></Form.Label>
                  <Form.Select
                    value={reasonforVisit}
                    onChange={(e) => setReasonforVisit(e.target.value)}
                    className="p-2.5 rounded-3 border-light shadow-sm text-secondary"
                  >
                    <option value="" disabled>Select reason configuration...</option>
                    {reasonforvisitOptions.map((reason, index) => (
                      <option key={index} value={reason}>{reason}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <h6 className="text-primary fw-bold mb-3 small uppercase tracking-wider">3. Financial Framework Metrics</h6>
            <Row className="g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="text-secondary small fw-medium mb-1">Advance Escrow Deposit Amount (INR)</Form.Label>
                  <Form.Control 
                    type="number" 
                    placeholder="0.00"
                    value={additional.advancePayment}
                    onChange={(e) => setAdditional({ ...additional, advancePayment: e.target.value })}
                    className="p-2.5 rounded-3 border-light shadow-sm"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="text-secondary small fw-medium mb-1">Encounter Initialization Status</Form.Label>
                  <Form.Select
                    value={additional.status}
                    onChange={(e) => setAdditional({ ...additional, status: e.target.value })}
                    className="p-2.5 rounded-3 border-light shadow-sm text-secondary"
                  >
                    <option value="">Choose status tag...</option>
                    <option value="Confirmed">Confirmed Entry</option>
                    <option value="Pending">Pending Verification</option>
                    <option value="Triage">In Triage</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        
        <Modal.Footer className="border-0 pt-0 pb-4 px-4 bg-white gap-2">
          {edit && (
            <Button variant="outline-danger" className="me-auto px-4 py-2 rounded-3 fw-medium" onClick={deleteAppointmentHandler}>
              Purge Reservation
            </Button>
          )}
          <Button variant="light" className="px-4 py-2 rounded-3 text-secondary border" onClick={toggle}>
            Dismiss
          </Button>
          <Button 
            variant="primary" 
            className="px-4 py-2 rounded-3 shadow-sm d-flex align-items-center gap-2 fw-medium"
            onClick={edit ? updateAppointmentHandler : bookingHandler}
            disabled={isSaving}
          >
            {isSaving ? <Spinner size="sm" animation="border" /> : edit ? "Save Configuration" : "Commit Secure Slot"}
          </Button>
        </Modal.Footer>
      </Modal>
    </motion.div>
  );
};

export default Booking;